import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import type { EmojiClickData, Theme } from 'emoji-picker-react';

/**
 * Gebrandetes Emoji-Auswahl-Popover (Wrapper um emoji-picker-react).
 *
 * Die Lib ist relativ schwer (Emoji-Daten) und wird daher LAZY geladen — der Default-Export
 * wird per dynamischem Import in einen <Suspense> gehängt, damit der Code erst beim ersten
 * Öffnen ins Bundle/Netz fällt und der vite-Build sauber code-splittet.
 *
 * POSITIONIERUNG: Das Panel wird per PORTAL an <body> gehängt und mit `position: fixed`
 * relativ zum Auslöser (`anchorRef`) platziert. So wird es NIE vom Chat-Scrollcontainer
 * (overflow-y-auto) abgeschnitten — auch nicht bei der obersten Nachricht. Es kippt
 * automatisch nach UNTEN oder OBEN, je nachdem wo Platz ist, und wird horizontal in den
 * Viewport geklemmt. Beim Scrollen/Resizen wird neu positioniert.
 *
 * Esc und ein Klick außerhalb (Panel UND Auslöser) rufen onClose(). `onPick` schließt das
 * Panel ABSICHTLICH NICHT — der Aufrufer entscheidet, ob/wann nach einem Pick geschlossen wird.
 */

const Picker = lazy(() => import('emoji-picker-react'));

const PICKER_W = 320;
const PICKER_H = 360;
const MARGIN = 8;

interface EmojiPickerProps {
  /** Auslöser-Element, an dem das Popover ausgerichtet wird. */
  anchorRef: RefObject<HTMLElement | null>;
  onPick: (emoji: string) => void;
  onClose: () => void;
}

export function EmojiPicker({ anchorRef, onPick, onClose }: EmojiPickerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Position relativ zum Auslöser berechnen (mit Auto-Flip + Viewport-Clamping).
  useLayoutEffect(() => {
    function place() {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      // Bevorzugt unten öffnen; nach oben nur, wenn unten zu wenig und oben mehr Platz ist.
      const openBelow = spaceBelow >= PICKER_H + MARGIN || spaceBelow >= spaceAbove;
      let top = openBelow ? r.bottom + 4 : r.top - PICKER_H - 4;
      top = Math.max(MARGIN, Math.min(top, window.innerHeight - PICKER_H - MARGIN));
      let left = r.left + r.width / 2 - PICKER_W / 2;
      left = Math.max(MARGIN, Math.min(left, window.innerWidth - PICKER_W - MARGIN));
      setPos({ top, left });
    }
    // Reposition per rAF drosseln: bei Scroll-/Resize-Bursts wird `place` höchstens einmal
    // pro Frame ausgeführt (verhindert Layout-Thrashing durch viele getBoundingClientRect-Reads).
    let frame = 0;
    function onReposition() {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        place();
      });
    }

    place();
    // passive: true → der Listener blockiert das Scrollen nicht (reines Neupositionieren).
    // capture: true beim scroll → fängt auch das Scrollen im Chat-Container (nicht nur window-Scroll).
    window.addEventListener('resize', onReposition, { passive: true });
    window.addEventListener('scroll', onReposition, { capture: true, passive: true });
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, { capture: true });
    };
  }, [anchorRef]);

  // Esc + Klick außerhalb (Panel und Auslöser ausgenommen) schließen.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    }
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      onClose();
    }
    window.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose, anchorRef]);

  if (!pos) return null;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Emoji auswählen"
      style={{ position: 'fixed', top: pos.top, left: pos.left, width: PICKER_W }}
      className="z-[90] overflow-hidden rounded-xl border border-kalkuel-border bg-white shadow-brand-lg"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Suspense
        fallback={
          <div
            className="flex items-center justify-center text-kalkuel-muted"
            style={{ height: PICKER_H, width: PICKER_W }}
          >
            <Loader2 size={20} className="animate-spin" />
          </div>
        }
      >
        <Picker
          onEmojiClick={(data: EmojiClickData) => onPick(data.emoji)}
          lazyLoadEmojis
          // KEIN Auto-Fokus des Suchfelds → kein Scroll-Sprung beim Öffnen.
          autoFocusSearch={false}
          height={PICKER_H}
          width={PICKER_W}
          previewConfig={{ showPreview: false }}
          skinTonesDisabled
          theme={'light' as Theme}
        />
      </Suspense>
    </div>,
    document.body,
  );
}
